/**
 * Notices: things about this console that are worth knowing but do not block the page (the hub
 * runs other code, alerts cannot reach you). They used to stack as full-width banners on every
 * page; now they collapse into one top-bar status item, "2 notices", that opens a popover
 * (live brief, section 3). A full-width banner stays only for an error that blocks the current
 * page (the hub is gone), and for Catch, Recap and dunning.
 *
 * Any feature publishes one with `useNotice(notice | null)`; the shell renders the item. The
 * store is keyed by id, so publishing again replaces, and unmounting withdraws.
 */
import { useEffect, useRef } from 'react';
import { create } from 'zustand';

/** Attention: worth acting on. Danger: something you rely on is broken. Neutral: for information. */
export type NoticeTone = 'danger' | 'attention' | 'neutral';

/** What the notice is about, for its icon. 'incident' and 'watch' come from Orgus Watch (ADR 0058). */
export type NoticeKind = 'build' | 'alerts' | 'alerts-off' | 'info' | 'incident' | 'watch';

export interface NoticeAction {
  id: string;
  /** Verb plus object: "Turn on alerts", "Reload page". */
  label: string;
  /** Runs the action. A rejected promise shows its error inline under the notice. */
  run?: () => void | Promise<void>;
  /** Or a console path to open (the popover closes). */
  to?: string;
  /** The notice's main action gets `secondary`; the rest stay quiet. */
  emphasis?: 'secondary' | 'ghost';
}

export interface Notice {
  /** Stable id: one notice per id. */
  id: string;
  tone: NoticeTone;
  kind: NoticeKind;
  /** One sentence, what is going on: "Alerts are blocked in this browser". */
  title: string;
  /** Why it matters and what to do next. */
  body: string;
  /** A fact for whoever fixes it, set in mono (build ids). */
  detail?: string;
  actions?: readonly NoticeAction[];
  /** What failed the last time an action ran, shown inline (never a toast). */
  error?: unknown;
  /** Lower comes first among notices of the same tone. */
  order?: number;
  /**
   * When what it reports began (an incident's opening). A notice with one adds to the bell's count
   * only while it is newer than the bell's read mark: history a new hub finds, or one already seen
   * when the bell was opened, stays listed but does not count (QA-6). Without one it always counts.
   */
  since?: number;
}

interface NoticeStore {
  byId: Readonly<Record<string, Notice>>;
}

export const useNoticeStore = create<NoticeStore>()(() => ({ byId: {} }));

export function publishNotice(n: Notice): void {
  useNoticeStore.setState((s) => (s.byId[n.id] === n ? s : { byId: { ...s.byId, [n.id]: n } }));
}

export function withdrawNotice(id: string): void {
  useNoticeStore.setState((s) => {
    if (!(id in s.byId)) return s;
    const { [id]: _gone, ...rest } = s.byId;
    return { byId: rest };
  });
}

const TONE_RANK: Record<NoticeTone, number> = { danger: 0, attention: 1, neutral: 2 };

/** Danger first, then attention, then the rest; inside a tone by `order`, then id (stable). */
export function sortNotices(list: Iterable<Notice>): Notice[] {
  return [...list].sort((a, b) => TONE_RANK[a.tone] - TONE_RANK[b.tone] || (a.order ?? 0) - (b.order ?? 0) || a.id.localeCompare(b.id));
}

/** The notices that add to the bell's count: those without a start, and those newer than the read mark. */
export function countedNotices(list: readonly Notice[], lastSeen: number | null): Notice[] {
  return list.filter((n) => n.since === undefined || (lastSeen !== null && n.since > lastSeen));
}

/** The newest start among the notices, for the bell's read mark when it opens; 0 with none. */
export function newestNoticeSince(list: readonly Notice[]): number {
  let at = 0;
  for (const n of list) if (n.since !== undefined && n.since > at) at = n.since;
  return at;
}

/** The loudest tone among the notices, for the top-bar item's icon; null with none. */
export function topTone(list: readonly Notice[]): NoticeTone | null {
  let best: NoticeTone | null = null;
  for (const n of list) if (best === null || TONE_RANK[n.tone] < TONE_RANK[best]) best = n.tone;
  return best;
}

/** "1 notice", "2 notices". */
export function noticesLabel(n: number): string {
  return n === 1 ? '1 notice' : `${n} notices`;
}

/**
 * Publish a notice while the calling component is mounted and `notice` is not null. Build it
 * inline each render: it is re-published only when what people see changes, and its actions
 * always call the latest closures (no stale state in a button).
 */
export function useNotice(notice: Notice | null): void {
  const latest = useRef(notice);
  latest.current = notice;
  const key = notice ? noticeKey(notice) : null;
  useEffect(() => {
    const n = latest.current;
    if (!n) return;
    const call = (id: string) => latest.current?.actions?.find((a) => a.id === id)?.run?.();
    publishNotice({ ...n, actions: n.actions?.map((a) => (a.run ? { ...a, run: () => call(a.id) } : a)) });
    return () => withdrawNotice(n.id);
  }, [key]);
}

/** Everything a person sees in a notice, so a re-render with the same content does not re-publish. */
function noticeKey(n: Notice): string {
  const err = n.error instanceof Error ? n.error.message : n.error ? JSON.stringify(n.error) : '';
  return [n.id, n.tone, n.kind, n.title, n.body, n.detail ?? '', n.order ?? 0, err, ...(n.actions ?? []).map((a) => `${a.id}:${a.label}:${a.to ?? ''}:${a.emphasis ?? ''}`)].join('\u0001');
}
